<template>
  <nav class="sidebar h-full overflow-y-auto bg-gray-50 px-1.5 py-3 dark:bg-zinc-900">
    <button
        v-if="showCollapseToggle"
        type="button"
        class="sidebar-item mb-1 w-full rounded-lg px-3.5 py-2 text-left transition duration-200 text-zinc-700 hover:bg-gray-200 dark:text-zinc-200 dark:hover:bg-zinc-800 cursor-pointer"
        @click="$emit('toggle-collapse')"
    >
      <span class="sidebar-icon-cell">
        <i :class="collapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-left'"></i>
      </span>
      <span class="sidebar-item-label text-xs uppercase tracking-wide text-zinc-400">
        {{ $t('label.menu') }}
      </span>
    </button>

    <button
        v-for="(menuItem, index) in menuItems"
        :key="index"
        type="button"
        class="sidebar-item mb-1 w-full rounded-lg px-3.5 py-2 text-left transition duration-200 cursor-pointer"
        :class="[
          isActive(menuItem.to)
            ? 'bg-primary-600/20 text-primary-700 dark:bg-primary-500/20 dark:text-primary-400'
            : 'text-zinc-700 hover:bg-gray-200 dark:text-zinc-200 dark:hover:bg-zinc-800',
        ]"
        :title="menuItem.label"
        @click="navigate(menuItem)"
    >
      <span class="sidebar-icon-cell">
        <i :class="menuItem.icon" class="shrink-0"></i>
      </span>
      <span class="sidebar-item-label font-semibold">{{ menuItem.label }}</span>
    </button>
  </nav>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    showCollapseToggle: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['navigate', 'toggle-collapse', 'open-qr-scanner'],

  computed: {
    ...mapGetters([
      'user',
    ]),

    menuItems() {
      if (this.user?.mustChangePassword === true) {
        return [];
      }

      const items = [
        {
          labelKey: 'menu.projects',
          icon: 'fal fa-tasks',
          needsAuthentication: true,
          to: '/',
        },
        {
          labelKey: 'menu.time_registration',
          icon: 'fal fa-clock',
          needsAuthentication: true,
          to: '/uren-registratie',
        },
        {
          labelKey: 'menu.qr_scan',
          icon: 'fal fa-qrcode',
          needsAuthentication: true,
          action: 'open-qr-scanner',
          mobileOnly: true,
        },
        {
          labelKey: 'menu.department_management',
          icon: 'fal fa-sitemap',
          needsAuthentication: true,
          to: '/afdelingen',
        },
        {
          labelKey: 'menu.user_management',
          icon: 'fal fa-users-cog',
          needsAuthentication: true,
          to: '/gebruikers',
          roles: ['ROLE_ADMIN'],
        },
      ];

      if (!this.$store.getters['session/accessToken']) {
        return items
            .filter(menuItem => !menuItem.needsAuthentication)
            .map(menuItem => ({
              ...menuItem,
              label: this.$t(menuItem.labelKey),
            }));
      }

      return items
          .filter(menuItem => {
            let userRoles = [];
            if (Array.isArray(this.user?.roles)) {
              userRoles = this.user.roles;
            } else if (this.user?.role) {
              userRoles = [this.user.role];
            }

            if (menuItem.roles && !menuItem.roles.some(role => userRoles.includes(role))) {
              return false;
            }
            if (menuItem.mobileOnly && !this.isMobile) {
              return false;
            }

            return menuItem.needsAuthentication !== false;
          })
          .map(menuItem => ({
            ...menuItem,
            label: this.$t(menuItem.labelKey),
          }));
    },
  },

  methods: {
    isActive(path) {
      if (!path) {
        return false;
      }

      if (path === '/') {
        return this.$route.path === '/' || this.$route.path.startsWith('/projects/');
      }

      return this.$route.path === path || this.$route.path.startsWith(`${path}/`);
    },

    navigate(menuItem) {
      if (menuItem.action === 'open-qr-scanner') {
        this.$emit('open-qr-scanner');
        this.$emit('navigate');
        return;
      }

      if (this.$route.path !== menuItem.to) {
        this.$router.push(menuItem.to);
      }

      this.$emit('navigate');
    },
  },
};
</script>

<style scoped>
.sidebar-item {
  display: flex;
  align-items: center;
  overflow: hidden;
  line-height: 1;
  min-height: 2.75rem;
}

.sidebar-icon-cell {
  width: 1.25rem;
  min-width: 1.25rem;
  max-width: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-item-label {
  margin-left: 0.625rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
